import algoliasearch from "algoliasearch/lite";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";

export interface SearchConfig {
    // Propriétés communes
    indexName: string;
    driver: string;

    // Propriétés Algolia
    appId?: string;
    searchKey?: string;

    // Propriétés Typesense
    apiKey?: string;
    host?: string;
    port?: string;
    protocol?: string;
    // Nœud interne utilisé côté serveur (SSR) quand il diffère de l'endpoint public du navigateur
    host2?: string;
    port2?: string;
    protocol2?: string;
}

// Interface pour les paramètres de recherche Typesense
export interface TypesenseSearchParams {
    query_by?: string;
    facet_by?: string;
    max_facet_values?: number;
    search_type?: string;
    num_typos?: number;
    prefix?: boolean;
    infix?: string;
    query_by_weights?: string;
    cacheSearchResultsForSeconds?: number;
    sort_by?: string;
    min_len_1typo?: number;
    min_len_2typo?: number;
    drop_tokens_threshold?: number;
    typo_tokens_threshold?: number;
    cache_ttl?: number;
    use_cache?: boolean;
    include_fields?: string;
    exclude_fields?: string;
}

/**
 * Champs nécessaires pour afficher VehicleCard
 * Utilisé pour limiter la taille des réponses Typesense
 *
 * Optimisations appliquées :
 * - Pas de `model` ni `specification` : on utilise display_name2 pour l'alt
 * - Pas de `local_photos_url` : VehicleAlgoliaRecord utilise `local_photos`
 */
const VEHICLE_CARD_FIELDS = [
    "id",
    "objectID",
    "slug",
    "make",
    "model",
    "specification",
    "monthly_price",
    "price",
    "price_before_promotion",
    "has_promotion",
    "promotion_value",
    "energy_type",
    "registration_year",
    "photos",
    "photos_count",
    "mileage",
    "power",
    "registration_at",
    "display_name2",
    "local_photos",
].join(",");

export class SearchClientFactory {
    static create(config: SearchConfig): any {
        const driverName = config.driver;

        if (driverName === "typesense") {
            return SearchClientFactory.createTypesenseClient(config);
        } else {
            return SearchClientFactory.createAlgoliaClient(config);
        }
    }

    private static createAlgoliaClient(config: SearchConfig) {
        if (!config.appId || !config.searchKey) {
            throw new Error(
                "Algolia configuration manquante : appId et searchKey sont requis",
            );
        }

        return algoliasearch(config.appId, config.searchKey);
    }

    /**
     * Configuration de base commune pour Typesense
     */
    private static getBaseTypesenseConfig(): TypesenseSearchParams {
        return {
            query_by: "make,model,specification",
            max_facet_values: 500,
            query_by_weights: "make:10,model:10,specification:10",
            cacheSearchResultsForSeconds: 2 * 60, // Cache 2 minutes
        };
    }

    /**
     * Configuration par défaut pour la recherche principale (stricte)
     */
    private static getDefaultSearchConfig(): TypesenseSearchParams {
        return {
            ...this.getBaseTypesenseConfig(),
            facet_by: [
                "make",
                "model",
                "specification",
                "registration_at",
                "energy_type",
                "price",
                "monthly_price",
                "mileage",
                "reference_exterior_color",
                "options",
                "reference_interior_color",
                "reference_interior_material",
                "weight",
                "co2_emission",
                "power",
                "seats",
                "body_type",
                "has_promotion",
                // TODO: Activer après normalisation des données gearbox
                // "gearbox",
            ].join(","),
            // Configuration pour recherches exactes par défaut
            num_typos: 0,
            prefix: false,
            infix: "off",
            // Tri par défaut sur marketplace_score (round-robin par marque)
            sort_by: "marketplace_score:asc",
            // Limiter les champs retournés pour réduire la taille du payload
            include_fields: VEHICLE_CARD_FIELDS,
        };
    }

    /**
     * Configuration optimisée pour l'autocomplete (simplifiée pour SL avant SLR)
     */
    private static getAutocompleteSearchConfig(): TypesenseSearchParams {
        return {
            ...this.getBaseTypesenseConfig(),
            //facet_by: "make,model,specification", // Moins de facettes pour l'autocomplete
            query_by:
                "suggestion_make,suggestion_make_model,suggestion_make_model_specification",
            prefix: true,
            query_by_weights:
                "suggestion_make:10,suggestion_make_model:6,suggestion_make_model_specification:3",
            // Tri alphabétique pour avoir SL avant SLR
            sort_by: "visit_count:desc,_text_match:desc",
            num_typos: 0,
            min_len_1typo: 10,
            min_len_2typo: 10,
            drop_tokens_threshold: 0,
            typo_tokens_threshold: 0,
            infix: "fallback",
            cache_ttl: 10,
            use_cache: true,
        };
    }

    /**
     * Créer un client Typesense avec configuration personnalisée
     */
    private static createTypesenseClientWithConfig(
        config: SearchConfig,
        searchParams: TypesenseSearchParams,
    ) {
        if (!config.host || !config.apiKey) {
            throw new Error(
                "Typesense configuration manquante : host et apiKey sont requis",
            );
        }

        let host = config.host;
        let port = config.port;
        let protocol = config.protocol;

        // on server use host2 (and its port/protocol) if available
        // this is important for SSR to work as host can be different from the frontend host
        // (ex: http://typesense:8108 inside Docker vs https://pp-search.joinsteer.com:443 in the browser)
        // see env variables and containers configuration
        if (typeof window === "undefined" && config.host2) {
            host = config.host2;
            port = config.port2 ?? port;
            protocol = config.protocol2 ?? protocol;
        }

        // Breadcrumb Sentry pour tracer la collection (utile pour debug 404)
        if (typeof window === "undefined") {
            try {
                const Sentry = require("@sentry/node");
                Sentry.addBreadcrumb({
                    category: "typesense",
                    message: `Collection: ${config.indexName}`,
                    level: "info",
                    data: { host, indexName: config.indexName },
                });
            } catch {
                // Sentry pas dispo, on ignore
            }
        }

        const typesenseInstantSearchAdapter = new TypesenseInstantSearchAdapter(
            {
                server: {
                    apiKey: config.apiKey,
                    nodes: [
                        {
                            host: host,
                            port: parseInt(port || "8108"),
                            protocol: protocol || "http",
                        },
                    ],
                    cacheSearchResultsForSeconds:
                        searchParams.cacheSearchResultsForSeconds,
                },
                additionalSearchParameters: searchParams,
            },
        );

        // Wrapper pour enrichir les erreurs avec le nom de la collection
        const originalSearchClient =
            typesenseInstantSearchAdapter.searchClient as any;

        const enrichTypesenseError = (
            error: any,
            requests: any[],
            operation: string,
        ) => {
            const collectionNames = requests
                .map((r: any) => r.indexName)
                .filter(Boolean)
                .join(", ");
            const collection = collectionNames || config.indexName;
            const errorMsg = error.message || error;
            const enrichedMessage = `${errorMsg} [collection: ${collection}, operation: ${operation}]`;

            // Log côté serveur (SSR)
            if (typeof window === "undefined") {
                console.error(
                    `Typesense ${operation} error on collection "${collection}":`,
                    error,
                );
            }

            // Recréer l'erreur avec le message enrichi
            const enrichedError = new Error(enrichedMessage);
            enrichedError.name = error.name || "TypesenseSearchError";
            (enrichedError as any).originalError = error;
            (enrichedError as any).collection = collection;
            (enrichedError as any).operation = operation;
            return enrichedError;
        };

        const wrappedClient: any = {
            ...originalSearchClient,
            search: async (requests: any[]) => {
                try {
                    return await originalSearchClient.search(requests);
                } catch (error: any) {
                    throw enrichTypesenseError(error, requests, "search");
                }
            },
        };

        // Wrapper searchForFacetValues si disponible
        if (typeof originalSearchClient.searchForFacetValues === "function") {
            wrappedClient.searchForFacetValues = async (requests: any[]) => {
                try {
                    return await originalSearchClient.searchForFacetValues(
                        requests,
                    );
                } catch (error: any) {
                    throw enrichTypesenseError(
                        error,
                        requests,
                        "searchForFacetValues",
                    );
                }
            };
        }

        return wrappedClient;
    }

    private static createTypesenseClient(config: SearchConfig) {
        return this.createTypesenseClientWithConfig(
            config,
            this.getDefaultSearchConfig(),
        );
    }

    /**
     * Créer un client Typesense optimisé pour l'autocomplete
     */
    public static createAutocompleteClient(config: SearchConfig) {
        return this.createTypesenseClientWithConfig(
            config,
            this.getAutocompleteSearchConfig(),
        );
    }

    /**
     * Créer un client Typesense avec des paramètres personnalisés
     */
    public static createCustomTypesenseClient(
        config: SearchConfig,
        customParams: Partial<TypesenseSearchParams>,
    ) {
        const baseConfig = this.getBaseTypesenseConfig();
        const finalConfig = { ...baseConfig, ...customParams };

        return this.createTypesenseClientWithConfig(config, finalConfig);
    }
}
